import { v4 } from 'uuid';

import { RecordData } from '../../helpers-classes';
import { SkuAnnotationArrowhead, SkuAnnotationLineStyle, SkuAnnotationType } from './shared.enums';
import { ColorRun, SkuAnnotationData, SkuAnnotationDataIn, StyleRun } from './types';

type LegacyTextBoxFillOpacityParams = {
	backgroundColor: string;
	strokeWidth: number;
};

/**
 * `fillOpacity` is the only encoding of "is the interior painted" -- but text boxes persisted before the
 * field existed expressed their fill through `backgroundColor` alone, so an absent key is resolved ONCE
 * here into the opacity those records were actually drawn at. Keyed on absence, never on `0`, so a text box
 * the user deliberately set to no fill is never re-filled.
 */
const legacyTextBoxFillOpacity = (params: LegacyTextBoxFillOpacityParams): number => {
	const { backgroundColor, strokeWidth } = params;
	if (backgroundColor === '') return 0;
	if (backgroundColor !== '#ffffff') return 1;
	return strokeWidth > 0 ? 0.9 : 0.01;
};

export const skuAnnotationData = (
	contextEmail: string,
	params: Partial<SkuAnnotationData> | null = null,
	dataIn: SkuAnnotationDataIn = null
): SkuAnnotationData => {
	let rd: RecordData<SkuAnnotationDataIn> = null;

	if (dataIn === null) {
		rd = new RecordData<SkuAnnotationDataIn>(params);
	} else {
		rd = new RecordData<SkuAnnotationDataIn>(dataIn);
	}

	const type = rd.useNewOrDefault<SkuAnnotationType>('type', SkuAnnotationType.TextBox);
	const backgroundColor = rd.useNewOrDefault<string>('backgroundColor', '#ffffff');
	const strokeWidth = rd.useNewOrDefault<number>('strokeWidth', 2);
	const fillOpacity = typeof rd.data?.fillOpacity === 'undefined' && type === SkuAnnotationType.TextBox
		? legacyTextBoxFillOpacity({ backgroundColor, strokeWidth })
		: rd.useNewOrDefault<number>('fillOpacity', 0);

	const record: SkuAnnotationData = {
		id: rd.useNewOrPartial<string>('id', v4()),
		type,
		x: rd.useNewOrDefault<number>('x', 0),
		y: rd.useNewOrDefault<number>('y', 0),
		x2: rd.useNewOrDefault<number>('x2', 0),
		y2: rd.useNewOrDefault<number>('y2', 0),
		width: rd.useNewOrDefault<number>('width', 120),
		height: rd.useNewOrDefault<number>('height', 40),
		rotation: rd.useNewOrDefault<number>('rotation', 0),
		text: rd.useNewOrDefault<string>('text', ''),
		fontSize: rd.useNewOrDefault<number>('fontSize', 19),
		color: rd.useNewOrDefault<string>('color', '#000000'),
		backgroundColor,
		textColor: rd.useNewOrDefault<string>('textColor', '#333333'),
		colorRuns: rd.useNewOrDefault<Array<ColorRun>>('colorRuns', []),
		styleRuns: rd.useNewOrDefault<Array<StyleRun>>('styleRuns', []),
		fontWeight: rd.useNewOrDefault<string>('fontWeight', 'normal'),
		fontStyle: rd.useNewOrDefault<string>('fontStyle', 'normal'),
		fontFamily: rd.useNewOrDefault<string>('fontFamily', 'Arial'),
		textAlign: rd.useNewOrDefault<string>('textAlign', 'left'),
		verticalAlign: rd.useNewOrDefault<string>('verticalAlign', 'top'),
		fillOpacity,
		strokeWidth,
		lineStyle: rd.useNewOrDefault<SkuAnnotationLineStyle>('lineStyle', SkuAnnotationLineStyle.Solid),
		arrowhead: rd.useNewOrDefault<SkuAnnotationArrowhead>('arrowhead', SkuAnnotationArrowhead.None),
		points: rd.useNewOrDefault<Array<{ x: number; y: number }>>('points', []),
		pageNumber: rd.useNewOrDefault<number>('pageNumber', 0),
		groupId: rd.useNewOrDefault<string>('groupId', ''),
		linkedIslandId: rd.useNewOrDefault<string>('linkedIslandId', ''),
		createdBy: rd.useNewOrDefault<string>('createdBy', contextEmail),
		createDate: rd.useNewOrDefault<string>('createDate', new Date().toISOString())
	};

	return record;
};
